import React, {
  type FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { UserT } from "../../../../models/models";
import { useNavigate } from "react-router-dom";
import Button, { ButtonModeE } from "../../../../components/Button/Button";
import UserItem from "../../../../components/Header/components/UserItem/UserItem";
import { useAuth } from "../../../../utils/hooks/useAuth";
import { H1, P, Span } from "../../../../components/Text/Text";
import useAccount from "../../../../utils/hooks/useAccount";
import { clss } from "../../../../utils/helpers/styleHelpers";
import { useAppSelector } from "../../../../store/hooks";
import { selectIsLoading } from "../../../../store/selectors/appSelectors";
import Icon from "../../../../components/Icon/Icon";
import { RoutesE } from "../../../../routes/routesData";
import usePopup from "../../../../utils/hooks/usePopup";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import styles from "./../../login.module.scss";

type LoginFormPropTypes = {
  isAddAccount?: boolean;
};

const LoginForm: FC<LoginFormPropTypes> = memo(({ isAddAccount }) => {
  const navigate = useNavigate();
  const { login } = useAccount();
  const { loggedUsers } = useAuth();
  const isLoading = useAppSelector(selectIsLoading);
  const [name, setName] = useState("");
  const [pass, setPass] = useState("");
  const [nameError, setNameError] = useState("");
  const [passError, setPassError] = useState("");
  const [isPasswordFieldType, setIsPasswordFieldType] = useState(true);
  const { removePopup } = usePopup();
  const { t } = useTranslate();
  const usernameInputRef = useRef(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleUsernameFocusCRUTCH = () => {
    // need to be replaced by some elegant solution
    usernameInputRef.current.value = usernameInputRef.current.value;
  };

  useEffect(() => {
    usernameInputRef.current?.addEventListener(
      "click",
      handleUsernameFocusCRUTCH
    );

    return () => {
      usernameInputRef.current?.removeEventListener(
        "click",
        handleUsernameFocusCRUTCH
      );
    };
  }, []);

  // useEffect(() => {
  //   if (name && pass) {
  //     const nameInput = document.getElementById('name');
  //     const passInput = document.getElementById('pass');
  //     if (nameInput && passInput) {
  //       nameInput.classList.remove(styles.unvisitedAutoFillInput);
  //       nameInput.classList.add(styles.visitedAutoFillInput);
  //       passInput.classList.remove(styles.unvisitedAutoFillInput);
  //       passInput.classList.add(styles.visitedAutoFillInput);
  //     }
  //   }
  // }, [name, pass]);

  useEffect(() => {
    document.addEventListener("keypress", handleKeyPress);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, []);

  const handleKeyPress = useCallback((event) => {
    const activeElementName = (document.activeElement as any)?.name;
    if (
      event.key === "Enter" &&
      (activeElementName === "username" || activeElementName === "password")
    ) {
      handleSubmit();
    }
  }, []);

  const navigateToForgotPassword = () => {
    navigate(RoutesE.FORGOT_PASSWORD);
    removePopup();
  };

  const navigateToHome = () => {
    navigate(RoutesE.HOME);
  };

  // const handleSelectUser = (userId: string) => {
  // markUserAsLoggedById(userId);
  // navigateToHome();
  // };

  const handleChangeInput = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (name === "username") {
      setName(value);
      setNameError("");
    } else if (name === "password") {
      setPass(value);
      setPassError("");
    }
  };

  const handleChangeRemember = (e: React.ChangeEvent<any>) => {
    const { checked } = e.target;
    if (checked) {
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberMe");
    }
  };

  const handleSubmit = async () => {
    const recaptcha_token = (await executeRecaptcha?.()) || "";

    const nameInput = document.getElementById("username") as HTMLInputElement;
    const passInput = document.getElementById("password") as HTMLInputElement;

    if (!nameInput.value) return setNameError("Field is required");
    if (!passInput.value) return setPassError("Field is required");

    const res = await login({
      username: nameInput.value,
      password: passInput.value,
      recaptcha_token,
    });
  };

  return (
    <div>
      <div
        className={clss([
          isAddAccount ? styles.addAccountWrapper : styles.newAccountWrapper,
          styles.loginFormWrapper,
          isLoading ? styles.disabledWrapper : "",
        ])}
      >
        <H1 className={styles.loginHeader}>
          {isAddAccount ? "Add Account" : "Login"}
        </H1>
        <form onChange={handleChangeInput} id="loginForm">
          {/* <input autoComplete="false" name="hidden" type="text" style={{ visibility: 'hidden', height: 0 }} /> */}
          <input
            type="text"
            id="username"
            name="username"
            value={name}
            className={clss([
              nameError ? styles.errorField : "",
              styles.unvisitedAutoFillInput,
            ])}
            ref={usernameInputRef}
            autoComplete="username"
            placeholder={t`Username`}
          />
          {nameError && <P className={styles.errorText}>{nameError}</P>}
          <div className={styles.passwordWrapper}>
            <input
              type={isPasswordFieldType ? "password" : "text"}
              id="password"
              name="password"
              value={pass}
              className={clss([
                nameError ? styles.errorField : "",
                styles.unvisitedAutoFillInput,
              ])}
              autoComplete="current-password"
              placeholder={t`Password`}
            />
            <Icon
              name={isPasswordFieldType ? "eye" : "eyeClosed"}
              onClick={() => setIsPasswordFieldType((prev) => !prev)}
              className={styles.eyeIcon}
            />
          </div>
          {passError && <P className={styles.errorText}>{passError}</P>}
        </form>
        <div className={styles.loginFormRow}>
          <div className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              className={styles.checkbox}
              onChange={handleChangeRemember}
              defaultChecked={!!localStorage.getItem("rememberMe") || true}
            />
            <span className={styles.mark} />
            <Span>Remember me</Span>
          </div>
          <Button
            onClick={navigateToForgotPassword}
            mode={ButtonModeE.LINK}
            link={RoutesE.FORGOT_PASSWORD}
          >
            Forgot password
          </Button>
        </div>
        <Button
          useGlobalLoading
          onClick={handleSubmit}
          disabled={!name && !pass}
        >
          Login
        </Button>
        {!isAddAccount && (
          <Button
            mode={ButtonModeE.LINK}
            onClick={navigateToHome}
            link={RoutesE.HOME}
          >
            Back to website
          </Button>
        )}
      </div>

      {!!loggedUsers?.length && !isAddAccount && (
        <div className={styles.userListWrapper}>
          <P className={styles.subtitle}>Or choose existing account</P>
          <div className={styles.userList}>
            {loggedUsers.map((user: UserT) => (
              <div
                // onClick={() => handleSelectUser(user.id)}
                key={user.id}
                className={styles.userItemWrapper}
              >
                <UserItem user={user} profilePhotoSrc={user.profilePhoto} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default LoginForm;
